import React, { useState, useEffect, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import { CURRENCY_UNITS } from "../../constants/global.constants";
import IconWithTooltip from "../../common/styles/components/headings/IconWithTooltip";
import UtilityCostFields from "../../components/UtilityCosts/UtilityCostFields";
import ChemicalSelectionFields from "../../components/ChemicalSelectionFields";

import {
  COST_INFORMATION,
  getFieldUnit,
  getConvertedFieldValue,
} from "./costInformationFields.config";

import "./index.scss";

const CostInformation = React.forwardRef((props, ref) => {
  const { t } = useTranslation();
  const { disabled = false, setMandatoryComponentsStatus } = props;

  const {
    trainUtilityCosts,
    trainChemicalSelection,
    conversion_rate,
    currency,
    volume,
    mass,
  } = useSelector((state) => state);

  const costValues = useMemo(
    () => ({
      costOfWater: trainUtilityCosts?.water_price || "",
      costOfElectricity: trainUtilityCosts?.energy_price || "",
      costOfDowntime: trainUtilityCosts?.downtime_cost || "",
    }),
    [trainUtilityCosts],
  );

  const [errors, setErrors] = useState({});
  const [costFields, setCostFields] = useState(
    COST_INFORMATION(conversion_rate, currency, volume, mass),
  );

  const [noError, setNoError] = useState(true);
  const dispatch = useDispatch();

  // Track previous units to detect changes
  const prevUnitsRef = useRef({ mass, volume, currency, conversion_rate });
  const isInitialMountRef = useRef(true);

  useEffect(() => {
    const noErrorVal = Object.values(errors).every(
      (error) => error === "" || error === null || error === undefined,
    );
    setNoError(noErrorVal);

    // Skip status update on initial mount
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      return;
    }

    // Update mandatory component status based on validation
    if (setMandatoryComponentsStatus) {
      const allFieldsFilled = Object.values(costValues).every(
        (value) => value !== "" && value !== null && value !== undefined,
      );

      const newFulfilled = noErrorVal && allFieldsFilled;
      const newError = !noErrorVal || !allFieldsFilled;

      // Only update if fulfilled or error changed
      setMandatoryComponentsStatus((prev) => {
        if (
          prev.costInformation.fulfilled !== newFulfilled ||
          prev.costInformation.error !== newError
        ) {
          return {
            ...prev,
            costInformation: {
              fulfilled: newFulfilled,
              changed: prev.costInformation.changed,
              error: newError,
            },
          };
        }
        return prev;
      });
    }
  }, [errors, costValues]);

  // Update cost fields and convert values when units change (not when values change)
  useEffect(() => {
    const mappedVolume = volume === "m³" ? "m3" : volume;
    const newCostFields = COST_INFORMATION(
      conversion_rate,
      currency,
      mappedVolume,
      mass,
    );
    setCostFields(newCostFields);

    // Check if units actually changed (not just first render)
    const prevUnits = prevUnitsRef.current;
    if (
      prevUnits &&
      (prevUnits.mass !== mass ||
        prevUnits.volume !== volume ||
        prevUnits.currency !== currency ||
        prevUnits.conversion_rate !== conversion_rate)
    ) {
      // Convert all existing values when units change
      const convertedValues = {};
      const prevMappedVolume =
        prevUnits.volume === "m³" ? "m3" : prevUnits.volume;

      Object.keys(costValues).forEach((fieldKey) => {
        if (costValues[fieldKey] != null && costValues[fieldKey] !== "") {
          const converted = getConvertedFieldValue(
            costValues[fieldKey],
            fieldKey,
            prevUnits.mass,
            prevMappedVolume,
            prevUnits.currency,
            prevUnits.conversion_rate,
            mass,
            mappedVolume,
            currency,
            conversion_rate,
          );
          convertedValues[fieldKey] = converted;
        }
      });
    }

    // Update previous units
    prevUnitsRef.current = { mass, volume, currency, conversion_rate };
  }, [conversion_rate, currency, volume, mass]);

  const costInformation = () => {
    const fieldKeys = Object.keys(costFields);
    const newFieldErrors = {};
    let hasEmptyFields = false;

    fieldKeys.forEach((field) => {
      const value = costValues[field];
      if (value === "" || value === null || value === undefined) {
        newFieldErrors[field] = t("ValueStrings.requiredField");
        hasEmptyFields = true;
      }
    });

    if (hasEmptyFields) {
      setErrors(newFieldErrors);
      return;
    }
  };

  const handleInputChange = (field, value) => {
    // Extract value from event if necessary
    let actualValue = value;
    if (value && typeof value === "object" && value.target) {
      actualValue = value.target.value;
    }

    setErrors((prev) => ({ ...prev, [field]: null })); // Clear error first

    // Mark component as changed
    if (setMandatoryComponentsStatus) {
      setMandatoryComponentsStatus((prev) => ({
        ...prev,
        costInformation: { ...prev.costInformation, changed: true },
      }));
    }

    // Get field configuration for validation
    const fieldConfig = costFields[field];

    // Handle empty input
    if (
      actualValue === "" ||
      actualValue === null ||
      actualValue === undefined
    ) {
      setErrors((prev) => ({
        ...prev,
        [field]: t("ValueStrings.requiredField"),
      }));
      return;
    }

    const numericValue = parseFloat(actualValue);

    // Immediate range validation
    if (!isNaN(numericValue) && fieldConfig) {
      const minVal = fieldConfig.ranges?.hard?.min || 0;
      const maxVal = fieldConfig.ranges?.hard?.max || 999999;

      if (numericValue < minVal || numericValue > maxVal) {
        setErrors((prev) => ({
          ...prev,
          [field]: `Out of Range ${minVal} – ${maxVal}`,
        }));
      }
    }
  };

  React.useImperativeHandle(ref, () => ({
    costInformation,
    hasErrors: () =>
      !noError || Object.values(errors).some((error) => error && error !== ""),
  }));

  return (
    <div className="ro-card-wrapper position-relative cost-information-card">
      <div className="ro-card-header">
        <div className="info-section">
          <div className="teal-heading ">
            {t("costInformationStrings.constInformation")}
          </div>
          {!disabled && (
            <IconWithTooltip label={t("helpInformation.costInformationHelp")} />
          )}
        </div>
      </div>
      <div className="cost-information-sections">
        <div className="cost-information-section">
          <div className="cost-information-section-header">
            {t("costInformationStrings.chemicalCosts")}
          </div>
          <ChemicalSelectionFields
            selectedChemicals={trainChemicalSelection}
            currencyUnit={currency ? CURRENCY_UNITS[currency] : "$"}
            mass={mass}
            disabled={disabled}
            containerClassName="cost-info-chemical-costs"
          />
        </div>

        <div className="cost-information-section">
          <div className="cost-information-section-header">
            {t("costInformationStrings.utilityCosts")}
          </div>
          <UtilityCostFields
            values={costValues}
            currency={currency}
            volume={volume}
            disabled={disabled}
            containerClassName="cost-info-utility-costs"
          />
        </div>
      </div>
    </div>
  );
});

export default CostInformation;
